import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';
import fcmService from '../services/fcmService';

const POLLING_INTERVAL = 15000; // 15 seconds - increased from 5 for better performance
const MAX_SOUND_FREQUENCY = 3000; // Prevent sound spam (3 seconds between sounds)

export const useNotifications = () => {
  const { user, token } = useSelector((state) => state.auth);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newNotifications, setNewNotifications] = useState([]);
  
  const pollingIntervalRef = useRef(null);
  const lastSoundTimeRef = useRef(0);
  const previousUnreadCountRef = useRef(0);
  const previousIdsRef = useRef(new Set());
  const audioContextRef = useRef(null);

  // Initialize audio context (requires user interaction)
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play notification sound based on user role
  const playNotificationSound = useCallback((role) => {
    const now = Date.now();
    
    // Prevent sound spam
    if (now - lastSoundTimeRef.current < MAX_SOUND_FREQUENCY) {
      return;
    }
    
    lastSoundTimeRef.current = now;

    try {
      const audioContext = initAudio();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different sounds for different roles
      switch (role) {
        case 'passenger':
          // Soft chime
          oscillator.frequency.value = 800;
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.5);
          break;
        
        case 'driver':
          // Loud alert (double beep)
          oscillator.frequency.value = 1000;
          gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.2);
          
          // Second beep
          setTimeout(() => {
            const osc2 = audioContext.createOscillator();
            const gain2 = audioContext.createGain();
            osc2.connect(gain2);
            gain2.connect(audioContext.destination);
            osc2.frequency.value = 1200;
            gain2.gain.setValueAtTime(0.5, audioContext.currentTime);
            gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
            osc2.start(audioContext.currentTime);
            osc2.stop(audioContext.currentTime + 0.2);
          }, 300);
          break;
        
        case 'admin':
          // Short beep
          oscillator.frequency.value = 600;
          gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
          break;
        
        default:
          // Default sound
          oscillator.frequency.value = 700;
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.4);
      }
    } catch (err) {
      console.error('Failed to play sound:', err);
    }
  }, [initAudio]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token || !user) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/notifications');
      const { notifications: newNotifications, unread_count } = response.data;
      
      setNotifications(newNotifications);
      
      // Detect notifications that weren't seen before
      const currentIds = new Set(newNotifications.map(n => n.id));
      const brandNew = newNotifications.filter(
        n => !n.read_at && !previousIdsRef.current.has(n.id)
      );
      
      // Only trigger toasts/sound if we've already done a first load (previousIds populated)
      if (previousIdsRef.current.size > 0 && brandNew.length > 0) {
        setNewNotifications(brandNew);
        playNotificationSound(user.role);
      } else if (previousIdsRef.current.size === 0 && unread_count > 0) {
        // On first load, play sound once if there are unread
        playNotificationSound(user.role);
      }
      previousIdsRef.current = currentIds;
      
      setUnreadCount(unread_count);
      previousUnreadCountRef.current = unread_count;
      
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, user, playNotificationSound]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;

    try {
      const response = await api.post('/notifications/read', {
        notification_id: notificationId,
      });
      
      setUnreadCount(response.data.unread_count);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [token]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return;

    try {
      await api.post('/notifications/read-all');
      
      setUnreadCount(0);
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: new Date().toISOString() }))
      );
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [token]);

  // Start polling when component mounts and user is authenticated
  useEffect(() => {
    if (!token || !user) {
      return;
    }

    // Fetch immediately
    fetchNotifications();

    // Start polling
    pollingIntervalRef.current = setInterval(fetchNotifications, POLLING_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [token, user, fetchNotifications]);

  // Stop polling when page is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Stop polling when tab is inactive
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      } else {
        // Resume polling when tab becomes active
        if (token && user) {
          fetchNotifications();
          pollingIntervalRef.current = setInterval(fetchNotifications, POLLING_INTERVAL);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, user, fetchNotifications]);

  // Initialize audio on first user interaction
  useEffect(() => {
    const initializeAudio = () => {
      initAudio();
      document.removeEventListener('click', initializeAudio);
      document.removeEventListener('touchstart', initializeAudio);
    };

    document.addEventListener('click', initializeAudio, { once: true });
    document.addEventListener('touchstart', initializeAudio, { once: true });

    return () => {
      document.removeEventListener('click', initializeAudio);
      document.removeEventListener('touchstart', initializeAudio);
    };
  }, [initAudio]);

  // ── Firebase FCM ────────────────────────────────────────────────────────────
  // Register FCM token when user logs in; set up foreground message handler.
  useEffect(() => {
    if (!user || !token) return;

    let unsubscribeForeground = null;

    const initFCM = async () => {
      try {
        await fcmService.init();

        // While the app tab is open (foreground), FCM bypasses the SW.
        // Re-fetch notifications so the bell icon updates immediately.
        unsubscribeForeground = await fcmService.onForegroundMessage((payload) => {
          // Update the in-app bell
          fetchNotifications();
          // Also show OS tray notification (FCM skips SW when tab is open)
          fcmService.showForegroundNotification(payload);
        });
      } catch (e) {
        // FCM errors should never break the rest of the app
        console.warn('[FCM] Init error:', e.message);
      }
    };

    initFCM();

    return () => {
      if (typeof unsubscribeForeground === 'function') {
        unsubscribeForeground();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    newNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
};
